import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import reportService from '../../../services/reportService';
import { generateResultsReportExcel } from '../../../services/resultsReportExportService';
import ExportProgressModal from '../../../components/Common/ExportProgressModal';
import { getProjects } from '../../../services/projectsService';
import { getMilestones } from '../../../services/milestonesService';
import continuousServiceService from '../../../services/continuousServiceService';
import salesContinuousServiceService from '../../../services/salesContinuousServiceService';
import { useModuleSettings } from '../../../contexts/ModuleSettingsContext';
import Toast from '../../../components/Common/Toast';
import { buildComparativeRowsFromByMonth, buildHierarchyTree, buildDepreciationHierarchy } from './resultsSideComputations';
import { useReportExpandState } from './useReportExpandState';

const formatoCantidad = new Intl.NumberFormat('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
const formatoMonto = new Intl.NumberFormat('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 });
const formatoMontoVariable = new Intl.NumberFormat('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 4 });

const compareProductCategoryName = (a, b) => {
  const nameA = a.category_name || 'Sin clasificaci�n';
  const nameB = b.category_name || 'Sin clasificaci�n';
  if (nameA === 'Sin clasificaci�n') return 1;
  if (nameB === 'Sin clasificaci�n') return -1;
  return nameA.localeCompare(nameB, 'es');
};

const Results = () => {
  const { activosEnabled } = useModuleSettings();
  const [mode, setMode] = useState('by_period'); // by_period | by_milestones
  const now = new Date();
  const currentYear = now.getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear - i);
  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];
  const [monthFrom, setMonthFrom] = useState('');
  const [yearFrom, setYearFrom] = useState(currentYear);
  const [monthTo, setMonthTo] = useState('');
  const [yearTo, setYearTo] = useState(currentYear);
  const [selectedProject, setSelectedProject] = useState('');
  const [milestones, setMilestones] = useState([]);
  const [selectedMilestoneIds, setSelectedMilestoneIds] = useState([]);
  const [filterProjectId, setFilterProjectId] = useState(''); // proyecto opcional en modo periodo
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportingResults, setExportingResults] = useState(false);
  const [exportProgress, setExportProgress] = useState(null);
  const [showExportProgress, setShowExportProgress] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [reportParams, setReportParams] = useState(null);
  const [productTrees, setProductTrees] = useState({});
  const [directTrees, setDirectTrees] = useState({});
  const [depTrees, setDepTrees] = useState({});
  const [expandedProductCategories, setExpandedProductCategories] = useState(() => new Set());
  const [modalReportSide, setModalReportSide] = useState('ventas'); // ventas | compras para modales
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const [showExitsDetailModal, setShowExitsDetailModal] = useState(false);
  const [exitsDetailLoading, setExitsDetailLoading] = useState(false);
  const [exitsDetail, setExitsDetail] = useState(null);
  const [exitsDetailProduct, setExitsDetailProduct] = useState(null); // { product_name, warehouse_name } para el t�tulo del modal
  const [showComprasDetailModal, setShowComprasDetailModal] = useState(false);
  const [comprasDetailProduct, setComprasDetailProduct] = useState(null); // { product_id, product_name }
  const [showServiciosDetailModal, setShowServiciosDetailModal] = useState(false);
  const [serviciosDetailClassification, setServiciosDetailClassification] = useState(null); // service_name string
  const [showCSDetailModal, setShowCSDetailModal] = useState(false);
  const [csDetailId, setCSDetailId] = useState(null);
  const [csDetailPeriod, setCSDetailPeriod] = useState(null); // month_key para filtrar avances por per�odo
  const [csDetailData, setCSDetailData] = useState(null);
  const [csDetailLoading, setCSDetailLoading] = useState(false);
  const [showProrrateoModal, setShowProrrateoModal] = useState(false);
  const [prorrateoDocRow, setProrrateoDocRow] = useState(null); // fila documento con service_start_date, service_end_date, total_amount, months, label

  useEffect(() => {
    getProjects().then((r) => {
      if (r.success && r.data) setProjects(r.data);
    });
  }, []);

  useEffect(() => {
    if (mode !== 'by_milestones' || !selectedProject) {
      setMilestones([]);
      setSelectedMilestoneIds([]);
      return;
    }
    getMilestones({ project_id: selectedProject }).then((r) => {
      if (r.success && r.data) setMilestones(r.data);
      else setMilestones([]);
    });
    setSelectedMilestoneIds([]);
  }, [mode, selectedProject]);

  const handleConsultar = async (e) => {
    e.preventDefault();
    let dateFrom = '';
    let dateTo = '';
    if (mode === 'by_period') {
      if (!monthFrom || !yearFrom || !monthTo || !yearTo) {
        setToast({ isOpen: true, message: 'Indique mes y a�o desde y hasta', type: 'error' });
        return;
      }
      const yFrom = parseInt(yearFrom, 10);
      const mFrom = parseInt(monthFrom, 10);
      const yTo = parseInt(yearTo, 10);
      const mTo = parseInt(monthTo, 10);
      if (yFrom > yTo || (yFrom === yTo && mFrom > mTo)) {
        setToast({ isOpen: true, message: 'El mes desde debe ser anterior o igual al mes hasta', type: 'error' });
        return;
      }
      dateFrom = `${yFrom}-${String(mFrom).padStart(2, '0')}-01`;
      // �ltimo d�a del mes: en JS el mes es 0-indexed, as� que mes 2 = marzo, d�a 0 = �ltimo d�a de febrero
      const lastDay = new Date(yTo, parseInt(mTo, 10), 0);
      dateTo = `${yTo}-${String(mTo).padStart(2, '0')}-${String(lastDay.getDate()).padStart(2, '0')}`;
    } else {
      if (!selectedProject) {
        setToast({ isOpen: true, message: 'Seleccione un proyecto', type: 'error' });
        return;
      }
      if (selectedMilestoneIds.length === 0) {
        setToast({ isOpen: true, message: 'Seleccione al menos un hito', type: 'error' });
        return;
      }
    }

    setLoading(true);
    try {
      const params = {
        mode,
        project_id: mode === 'by_period' ? (filterProjectId || undefined) : (selectedProject || undefined),
      };
      if (mode === 'by_period') {
        params.date_from = dateFrom;
        params.date_to = dateTo;
      } else {
        params.milestone_ids = selectedMilestoneIds;
      }
      const response = await reportService.getResultsReportSummary(params);
      if (response.success) {
        setReportData(response.data);
        setReportParams(params);
        setProductTrees({});
        setExpandedProductCategories(new Set());
      } else {
        setToast({ isOpen: true, message: response.message || 'Error al cargar el reporte', type: 'error' });
      }
    } catch (err) {
      setToast({
        isOpen: true,
        message: err.response?.data?.message || 'Error al cargar el reporte de resultados',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleMilestone = (id) => {
    setSelectedMilestoneIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const formatDate = (d) => {
    if (!d) return '-';
    const parsed = typeof d === 'string' && /^\d{4}-\d{1,2}-\d{1,2}/.test(d.trim()) ? parseLocalDate(d) : new Date(d);
    return parsed && !isNaN(parsed.getTime()) ? parsed.toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
  };

  const modalActiveReport = React.useMemo(() => {
    if (!reportData) return null;
    return modalReportSide === 'ventas' ? reportData.ventas : reportData.compras;
  }, [reportData, modalReportSide]);

  const reportPeriods = React.useMemo(
    () => (reportData?.periods || []).map((period) => ({
      month_key: period.key,
      date_from: period.date_from,
      date_to: period.date_to,
    })),
    [reportData],
  );

  const productCategoryKey = (side, category) => `${side}_${category?.category_id == null ? 0 : Number(category.category_id)}`;

  const loadProductCategory = React.useCallback(async (side, category) => {
    if (!reportParams) return;
    const key = productCategoryKey(side, category);
    setProductTrees((prev) => ({ ...prev, [key]: { status: 'loading' } }));
    try {
      const response = await reportService.getResultsReportDetail({
        ...reportParams,
        report_side: side,
        category_id: category?.category_id == null ? 0 : Number(category.category_id),
      });
      if (!response?.success) {
        throw new Error(response?.message || 'Error al cargar la categor�a');
      }
      const byMonth = response.data?.by_month || [];
      const rows = buildComparativeRowsFromByMonth(byMonth, { warehouse: true });
      setProductTrees((prev) => ({ ...prev, [key]: { status: 'loaded', tree: buildHierarchyTree(rows) } }));
    } catch (error) {
      console.error('Error al cargar la categor�a del reporte de resultados:', error);
      setProductTrees((prev) => ({ ...prev, [key]: { status: 'error' } }));
      setToast({ isOpen: true, message: 'No se pudo cargar la categor�a del reporte', type: 'error' });
    }
  }, [reportParams]);

  const toggleProductCategory = React.useCallback((side, category) => {
    const key = productCategoryKey(side, category);
    if (expandedProductCategories.has(key)) {
      setExpandedProductCategories((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      setProductTrees((prev) => {
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
      return;
    }
    setExpandedProductCategories((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
    if (!productTrees[key]) loadProductCategory(side, category);
  }, [expandedProductCategories, productTrees, loadProductCategory]);

  const depCategoryKey = (category) => `dep_${category?.category_id == null ? 0 : Number(category.category_id)}`;

  const loadDirectCategory = React.useCallback(async (side, category) => {
    if (!reportParams) return;
    const key = productCategoryKey(side, category);
    setDirectTrees((prev) => ({ ...prev, [key]: { status: 'loading' } }));
    try {
      const response = await reportService.getResultsReportDetail({
        ...reportParams,
        report_side: side,
        category_id: category?.category_id == null ? 0 : Number(category.category_id),
        section: 'direct',
      });
      if (!response?.success) {
        throw new Error(response?.message || 'Error al cargar la categor�a');
      }
      const rows = buildComparativeRowsFromByMonth(response.data?.by_month || [], { warehouse: false });
      setDirectTrees((prev) => ({ ...prev, [key]: { status: 'loaded', tree: buildHierarchyTree(rows) } }));
    } catch (error) {
      console.error('Error al cargar la categor�a de consumo directo:', error);
      setDirectTrees((prev) => ({ ...prev, [key]: { status: 'error' } }));
      setToast({ isOpen: true, message: 'No se pudo cargar la categor�a de consumo directo', type: 'error' });
    }
  }, [reportParams]);

  const loadDepCategory = React.useCallback(async (category) => {
    if (!reportParams) return;
    const key = depCategoryKey(category);
    setDepTrees((prev) => ({ ...prev, [key]: { status: 'loading' } }));
    try {
      const response = await reportService.getResultsReportDetail({
        ...reportParams,
        report_side: 'compras',
        category_id: category?.category_id == null ? 0 : Number(category.category_id),
        section: 'depreciaciones',
      });
      if (!response?.success) {
        throw new Error(response?.message || 'Error al cargar la categor�a');
      }
      setDepTrees((prev) => ({ ...prev, [key]: { status: 'loaded', tree: buildDepreciationHierarchy(response.data?.rows || []) } }));
    } catch (error) {
      console.error('Error al cargar la categor�a de depreciaciones:', error);
      setDepTrees((prev) => ({ ...prev, [key]: { status: 'error' } }));
      setToast({ isOpen: true, message: 'No se pudo cargar la categor�a de depreciaciones', type: 'error' });
    }
  }, [reportParams]);

  React.useEffect(() => {
    setDirectTrees({});
    setDepTrees({});
  }, [reportParams]);

  const ingExpand = useReportExpandState(reportData);
  const cosExpand = useReportExpandState(reportData);

  const openExitsDetailModal = async (productId, warehouseId, productName, warehouseName, apiSide = 'ventas') => {
    setModalReportSide(apiSide);
    if (!reportData?.date_from || !reportData?.date_to) return;
    const params = {
      mode,
      report_side: apiSide,
      project_id: mode === 'by_period' ? (filterProjectId || undefined) : (selectedProject || undefined),
    };
    if (mode === 'by_period') {
      params.date_from = reportData.date_from;
      params.date_to = reportData.date_to;
    } else {
      params.milestone_ids = selectedMilestoneIds;
    }
    if (productId != null && productId !== '') params.product_id = productId;
    if (warehouseId != null && warehouseId !== '') params.warehouse_id = warehouseId;
    setExitsDetailProduct(productName != null && warehouseName != null ? { product_name: productName, warehouse_name: warehouseName } : null);
    setShowExitsDetailModal(true);
    setExitsDetailLoading(true);
    setExitsDetail(null);
    try {
      const response = await reportService.getResultsExitsDetail(params);
      if (response.success && response.data) {
        setExitsDetail(response.data);
      } else {
        setToast({ isOpen: true, message: response.message || 'Error al cargar el detalle', type: 'error' });
      }
    } catch (err) {
      setToast({
        isOpen: true,
        message: err.response?.data?.message || 'Error al cargar el detalle de salidas',
        type: 'error',
      });
    } finally {
      setExitsDetailLoading(false);
    }
  };

  const closeExitsDetailModal = () => {
    setShowExitsDetailModal(false);
    setExitsDetail(null);
    setExitsDetailProduct(null);
  };

  const openComprasDetailModal = (productId, productName, apiSide = 'compras') => {
    setModalReportSide(apiSide);
    setComprasDetailProduct(productId != null && productName != null ? { product_id: productId, product_name: productName } : null);
    setShowComprasDetailModal(true);
  };

  const closeComprasDetailModal = () => {
    setShowComprasDetailModal(false);
    setComprasDetailProduct(null);
  };

  const openServiciosDetailModal = (serviceName, apiSide = 'ventas') => {
    setModalReportSide(apiSide);
    setServiciosDetailClassification(serviceName != null ? String(serviceName) : null);
    setShowServiciosDetailModal(true);
  };

  const closeServiciosDetailModal = () => {
    setShowServiciosDetailModal(false);
    setServiciosDetailClassification(null);
  };

  const openCSDetailModal = async (sourceId, monthKey) => {
    if (sourceId == null) return;
    setCSDetailId(sourceId);
    setCSDetailPeriod(monthKey != null ? String(monthKey) : null);
    setShowCSDetailModal(true);
    setCSDetailData(null);
    setCSDetailLoading(true);
    try {
      const response = modalReportSide === 'ventas'
        ? await salesContinuousServiceService.getContinuousService(sourceId)
        : await continuousServiceService.getContinuousService(sourceId);
      if (response.success && response.data) {
        setCSDetailData(response.data);
      } else {
        setToast({ isOpen: true, message: response.message || 'Error al cargar el servicio continuo', type: 'error' });
      }
    } catch (err) {
      setToast({ isOpen: true, message: err.response?.data?.message || 'Error al cargar el servicio continuo', type: 'error' });
    } finally {
      setCSDetailLoading(false);
    }
  };

  const closeCSDetailModal = () => {
    setShowCSDetailModal(false);
    setCSDetailId(null);
    setCSDetailPeriod(null);
    setCSDetailData(null);
  };

  const openProrrateoModal = (row) => {
    if (row?.source_type !== 'document' || !row?.service_start_date || !row?.service_end_date) return;
    setProrrateoDocRow(row);
    setShowProrrateoModal(true);
  };

  const closeProrrateoModal = () => {
    setShowProrrateoModal(false);
    setProrrateoDocRow(null);
  };

  // Parsear fecha YYYY-MM-DD como d�a local (evita desfase de un d�a por UTC)
  const parseLocalDate = (dateStr) => {
    if (!dateStr) return null;
    const s = String(dateStr).trim();
    const match = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (!match) return new Date(dateStr); // fallback
    const [, y, m, d] = match.map(Number);
    return new Date(y, m - 1, d);
  };

  const totalDaysInRange = (dateFrom, dateTo) => {
    const a = parseLocalDate(dateFrom);
    const b = parseLocalDate(dateTo);
    if (!a || !b || isNaN(a.getTime()) || isNaN(b.getTime())) return 0;
    const ms = Math.abs(b - a);
    return Math.floor(ms / (24 * 60 * 60 * 1000)) + 1;
  };

  const overlapDaysInPeriod = (serviceStart, serviceEnd, periodStart, periodEnd) => {
    const rs = parseLocalDate(serviceStart);
    const re = parseLocalDate(serviceEnd);
    const ps = parseLocalDate(periodStart);
    const pe = parseLocalDate(periodEnd);
    if (!rs || !re || !ps || !pe) return 0;
    const start = rs > ps ? rs : ps;
    const end = re < pe ? re : pe;
    if (end < start) return 0;
    return Math.floor((end - start) / (24 * 60 * 60 * 1000)) + 1;
  };

  const round4 = (value) => Math.round(Number(value || 0) * 10000) / 10000;

  // Para documentos, recalcular siempre en frontend con la misma f�rmula visible,
  // evitando cualquier desfase entre "months" recibido y el c�lculo mostrado.
  const getDocumentProratedAmount = (row, block) => {
    if (!row || !block || row.source_type !== 'document' || !row.service_start_date || !row.service_end_date) {
      return row?.months && row.months[block?.month_key] != null ? Number(row.months[block.month_key]) : 0;
    }
    const totalDays = totalDaysInRange(row.service_start_date, row.service_end_date);
    if (totalDays <= 0) return 0;
    const days = overlapDaysInPeriod(row.service_start_date, row.service_end_date, block.date_from, block.date_to);
    if (days <= 0) return 0;
    const totalAmount = Number(row.total_amount ?? 0);
    return round4((totalAmount * days) / totalDays);
  };

  const exportResultsToExcel = async () => {
    if (exportingResults) return;
    if (!reportData) {
      setToast({ isOpen: true, message: 'Primero consulte el reporte para exportar.', type: 'info' });
      return;
    }

    const params = {
        mode,
        project_id: mode === 'by_period' ? (filterProjectId || undefined) : (selectedProject || undefined),
      };
      if (mode === 'by_period') {
      params.date_from = reportData.date_from;
      params.date_to = reportData.date_to;
      } else {
      params.milestone_ids = selectedMilestoneIds;
    }

    setExportingResults(true);
    setShowExportProgress(true);
    setExportProgress({
      phase: 'background',
      status: 'processing',
      progress_percent: 5,
      message: 'Iniciando exportaci�n�',
    });

    try {
      await generateResultsReportExcel(params, { onProgress: setExportProgress });
      setToast({ isOpen: true, message: 'Excel generado correctamente', type: 'success' });
    } catch (error) {
      setToast({
        isOpen: true,
        message: error?.message || 'Error al exportar a Excel',
        type: 'error',
      });
    } finally {
      setExportingResults(false);
      setShowExportProgress(false);
      setExportProgress(null);
    }
  };

  const getMonthLabel = (monthKey) => {
    if (!monthKey) return '';
    const [y, m] = monthKey.split('-').map(Number);
    return `${monthNames[m - 1]} ${y}`;
  };

  const getPeriodLabel = (blockOrKey) => {
    const key = typeof blockOrKey === 'object' && blockOrKey != null ? blockOrKey.month_key : blockOrKey;
    if (!key) return '';
    const milestonePeriods = reportData?.milestone_periods || [];
    const milestone = milestonePeriods.find((p) => p.key === key);
    if (milestone) {
      return `${milestone.name} (${milestone.date_from} � ${milestone.date_to})`;
    }
    return getMonthLabel(key);
  };
  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <nav className="text-sm text-gray-600 mb-2">
          <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>
          <span className="mx-2">/</span>
          <Link to="/administration" className="hover:text-teal-600">Administraci�n</Link>
          <span className="mx-2">/</span>
          <span className="text-gray-800">Reportes</span>
          <span className="mx-2">/</span>
          <span className="text-gray-800">Resultados</span>
        </nav>
        <h1 className="text-3xl font-bold text-gray-800">Resultados</h1>
        <p className="mt-1 text-sm text-gray-500">
          Un solo reporte por per�odo: primero <span className="font-medium text-gray-700">ingresos</span> (ventas), luego{' '}
          <span className="font-medium text-gray-700">costos y gastos</span> (compras).
        </p>
      </div>

      {/* Filtros */}
      <form onSubmit={handleConsultar} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-end mb-4">
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="mode"
                checked={mode === 'by_period'}
                onChange={() => setMode('by_period')}
                className="text-teal-600 focus:ring-teal-500"
              />
              <span className="text-sm font-medium text-gray-700">Por periodo</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="mode"
                checked={mode === 'by_milestones'}
                onChange={() => setMode('by_milestones')}
                className="text-teal-600 focus:ring-teal-500"
              />
              <span className="text-sm font-medium text-gray-700">Por hitos</span>
            </label>
          </div>
        </div>

        {mode === 'by_period' && (
          <div className="flex flex-wrap gap-4 items-end mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Desde (mes / a�o)</label>
              <div className="flex gap-2">
                <select
                  value={monthFrom}
                  onChange={(e) => setMonthFrom(e.target.value)}
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm min-w-[120px]"
                >
                  <option value="">Mes</option>
                  {monthNames.map((name, i) => (
                    <option key={i + 1} value={i + 1}>{name}</option>
                  ))}
                </select>
                <select
                  value={yearFrom}
                  onChange={(e) => setYearFrom(Number(e.target.value))}
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm min-w-[90px]"
                >
                  {years.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Hasta (mes / a�o)</label>
              <div className="flex gap-2">
                <select
                  value={monthTo}
                  onChange={(e) => setMonthTo(e.target.value)}
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm min-w-[120px]"
                >
                  <option value="">Mes</option>
                  {monthNames.map((name, i) => (
                    <option key={i + 1} value={i + 1}>{name}</option>
                  ))}
                </select>
                <select
                  value={yearTo}
                  onChange={(e) => setYearTo(Number(e.target.value))}
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm min-w-[90px]"
                >
                  {years.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Proyecto</label>
              <select
                value={filterProjectId}
                onChange={(e) => setFilterProjectId(e.target.value)}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm min-w-[220px]"
              >
                <option value="">Todos los proyectos (todos los almacenes)</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.code} � {p.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {mode === 'by_milestones' && (
          <div className="flex flex-wrap gap-4 items-end mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Proyecto</label>
              <select
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm min-w-[220px]"
              >
                <option value="">Seleccione un proyecto</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.code} � {p.name}
                  </option>
                ))}
              </select>
            </div>
            {milestones.length > 0 && (
              <div className="flex-1 min-w-[280px]">
                <label className="block text-sm font-medium text-gray-700 mb-1">Hitos (seleccione uno o m�s)</label>
                <div className="flex flex-wrap gap-2 border border-gray-300 rounded-md p-2 bg-gray-50 max-h-32 overflow-y-auto">
                  {milestones.map((m) => (
                    <label key={m.id} className="flex items-center gap-2 cursor-pointer text-sm">
                      <input
                        type="checkbox"
                        checked={selectedMilestoneIds.includes(m.id)}
                        onChange={() => toggleMilestone(m.id)}
                        className="rounded text-teal-600 focus:ring-teal-500"
                      />
                      <span>
                        {m.name} ({formatDate(m.due_date)}
                        {m.end_date ? ` � ${formatDate(m.end_date)}` : ''})
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex flex-wrap gap-4 items-end mb-4">
          {mode === 'by_milestones' && (
            <div className="text-sm text-gray-600">
              Se calculan todos los almacenes para el proyecto e hitos seleccionados.
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-teal-600 text-white rounded-md text-sm font-medium hover:bg-teal-700 disabled:opacity-50"
          >
            {loading ? 'Consultando...' : 'Consultar'}
          </button>
        </div>
      </form>

      {reportData && (
        <>
          {reportData.date_from && reportData.date_to && (
            <div className="mb-4 flex items-center justify-between gap-3">
              <p className="text-sm text-gray-600">
                Per�odo: {formatDate(reportData.date_from)} � {formatDate(reportData.date_to)}
              </p>
              <button
                type="button"
                onClick={exportResultsToExcel}
                disabled={exportingResults}
                className="px-3 py-2 bg-emerald-600 text-white rounded-md text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-2"
              >
                {exportingResults && (
                  <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                )}
                {exportingResults ? 'Exportando...' : 'Descargar Excel'}
              </button>
            </div>
          )}

          {[
            {
              key: 'ventas',
              title: 'Ingresos',
              variant: 'ingresos',
              apiSide: 'ventas',
              expand: ingExpand,
              showDep: false,
            },
            {
              key: 'compras',
              title: 'Costos y gastos',
              variant: 'costos',
              apiSide: 'compras',
              expand: cosExpand,
              showDep: activosEnabled,
            },
          ].map((block) => {
            const blockReport = block.apiSide === 'ventas' ? reportData.ventas : reportData.compras;
            const byMonth = reportPeriods;
            const productCategories = [...(blockReport?.product_categories || [])].sort(compareProductCategoryName);
            const directCategories = [...(blockReport?.sin_almacen_categories || [])]
              .map((c) => ({ ...c, id: c.category_id ?? 0 }))
              .sort(compareProductCategoryName);
            const depCategories = [...(blockReport?.depreciaciones_categories || [])]
              .map((c) => ({ ...c, id: c.category_id ?? 0 }))
              .sort(compareProductCategoryName);
            const milestonePeriods = reportData?.milestone_periods || [];
            const depreciationPeriods =
              byMonth.length > 0
                ? byMonth
                : milestonePeriods.length > 0
                  ? milestonePeriods.map((p) => ({
                      month_key: p.key,
                      date_from: p.date_from,
                      date_to: p.date_to,
                    }))
                  : [];
            const {
              expandedDirectCategories,
              expandedDepCategories,
              toggleDirectCategory,
              toggleDepCategory,
            } = block.expand;

            const handleToggleDirectCategory = (category) => {
              const key = productCategoryKey(block.apiSide, category);
              const wasExpanded = expandedDirectCategories.has(category.id);
              toggleDirectCategory(category.id);
              if (wasExpanded) {
                setDirectTrees((prev) => {
                  const next = { ...prev };
                  delete next[key];
                  return next;
                });
              } else if (!directTrees[key]) {
                loadDirectCategory(block.apiSide, category);
              }
            };

            const handleToggleDepCategory = (category) => {
              const key = depCategoryKey(category);
              const wasExpanded = expandedDepCategories.has(category.id);
              toggleDepCategory(category.id);
              if (wasExpanded) {
                setDepTrees((prev) => {
                  const next = { ...prev };
                  delete next[key];
                  return next;
                });
              } else if (!depTrees[key]) {
                loadDepCategory(category);
              }
            };

            return (
              <div key={block.key} className="mb-12">
                <h2 className="text-2xl font-bold text-teal-800 border-b-2 border-teal-200 pb-2 mb-6">{block.title}</h2>

          {/* Secci�n 1: Salida de almac�n � comparativo por meses (Enero abajo cant/monto, Febrero abajo cant/monto, etc.) */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-2">
              Salida de almac�n
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Salidas del kardex comparativo por mes (las devoluciones en negativo). Expanda una categor�a para cargar sus subcategor�as, familias y productos. Haga clic en <strong>Ver detalle</strong> en una fila para ver todas las salidas de ese producto en ese almac�n.
            </p>

            {productCategories.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-500">
                No hay salidas en el per�odo seleccionado.
              </div>
            ) : (
              <div className="space-y-4">
                {productCategories.map((category) => {
                  const categoryKey = productCategoryKey(block.apiSide, category);
                  const categoryState = productTrees[categoryKey];
                  const isExpanded = expandedProductCategories.has(categoryKey);
                  const loadedCategory = categoryState?.tree?.[0];
                  const monthAmounts = category.by_month || {};
                  return (
                    <div key={`cat-${block.apiSide}-${categoryKey}`} className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
                    <div
                      className="bg-teal-50 px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-teal-100 transition-colors"
                        onClick={() => toggleProductCategory(block.apiSide, category)}
                    >
                      <div className="flex items-center gap-3">
                          {categoryState?.status === 'loading' ? (
                            <span className="inline-block w-5 h-5 border-2 border-teal-600 border-t-transparent rounded-full animate-spin" />
                          ) : (
                        <svg
                              className={`w-5 h-5 text-teal-600 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                          )}
                          <span className="font-semibold text-teal-800">{category.category_code || category.category_name}</span>
                          {category.category_code && <span className="text-gray-800 font-medium">{category.category_name}</span>}
                        <span className="text-xs text-gray-500">(Categor�a)</span>
                      </div>
                        <span className="text-sm text-gray-600">{category.subcategory_count} subcategor�a(s)</span>
                    </div>

                      <div className="border-t border-gray-200 overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              {byMonth.map((monthBlock) => (
                                <th key={monthBlock.month_key} className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                                  {getPeriodLabel(monthBlock)}
                                </th>
                              ))}
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="bg-white">
                              {byMonth.map((monthBlock) => {
                                const hasMonth = Object.prototype.hasOwnProperty.call(monthAmounts, monthBlock.month_key);
                                const amount = Number(monthAmounts[monthBlock.month_key] ?? 0);
                          return (
                                  <td key={monthBlock.month_key} className="px-3 py-2 text-sm text-right whitespace-nowrap">
                                    {hasMonth ? (
                                      <span className={amount < 0 ? 'text-red-600 font-medium' : 'text-gray-900'}>
                                        S/ {formatoMonto.format(amount)}
                                      </span>
                                    ) : (
                                      <span className="text-gray-300">�</span>
                                    )}
                                  </td>
                                );
                              })}
                              <td className="px-3 py-2 text-sm text-right whitespace-nowrap">
                                <span className={Number(category.total_amount ?? 0) < 0 ? 'text-red-600 font-semibold' : 'text-teal-900 font-semibold'}>
                                  S/ {formatoMonto.format(Number(category.total_amount ?? 0))}
                                </span>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      {isExpanded && (
                        <div className="border-t border-gray-200 bg-gray-50">
                          {categoryState?.status === 'loading' && (
                            <div className="px-6 py-4 text-sm text-gray-500">Cargando categor�a�</div>
                          )}

                          {categoryState?.status === 'error' && (
                            <div className="px-6 py-4 flex items-center justify-between text-sm text-red-600">
                              <span>No se pudo cargar la categor�a.</span>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  loadProductCategory(block.apiSide, category);
                                }}
                                className="font-medium text-red-700 underline hover:text-red-900"
                              >
                                Reintentar
                              </button>
                            </div>
                          )}

                          {categoryState?.status === 'loaded' && !loadedCategory && (
                            <div className="px-6 py-4 text-sm text-gray-500">Sin movimientos en el per�odo.</div>
                          )}

                          {loadedCategory && loadedCategory.subcategories.map((subcategory) => (
                            <div key={`sub-${subcategory.id}`} className="border-t border-gray-200 first:border-t-0">
                              <div className="bg-purple-50 px-6 py-2.5 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className="w-4 h-4 border-l-2 border-t-2 border-purple-300" />
                                  <span className="font-semibold text-purple-800">{subcategory.code || subcategory.name || '�'}</span>
                                  {subcategory.code && subcategory.name && <span className="text-gray-800">{subcategory.name}</span>}
                                  {!subcategory.code && !subcategory.name && <span className="text-gray-500">Sin subcategor�a</span>}
                                  <span className="text-xs text-gray-500">(Subcategor�a)</span>
                                </div>
                                <span className="text-sm text-gray-600">
                                  {subcategory.families.length} familia(s)
                                </span>
                              </div>

                                <div className="bg-gray-50/50">
                                  {subcategory.families.map((family) => (
                                    <div key={`fam-${family.id}`} className="border-t border-gray-200 first:border-t-0">
                                      <div className="px-8 py-2 flex items-center bg-gray-100/80 border-b border-gray-200">
                                        <div className="w-4 h-4 border-l-2 border-t-2 border-gray-400" />
                                        <span className="font-semibold text-gray-800 ml-2">{family.code || family.name || '�'}</span>
                                        {family.code && family.name && <span className="text-gray-700 ml-1">{family.name}</span>}
                                        {!family.code && !family.name && <span className="text-gray-500 ml-1">Sin familia</span>}
                                        <span className="text-xs text-gray-500 ml-2">(Familia) � {family.rows.length} producto(s)</span>
                                      </div>
                                      <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                          <thead className="bg-gray-100">
                                            <tr>
                                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Producto</th>
                                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Almac�n</th>
                                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-24">Detalle</th>
                                            {byMonth.map((monthBlock) => (
                                              <React.Fragment key={monthBlock.month_key}>
                                                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{getPeriodLabel(monthBlock)} (Cant.)</th>
                                                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{getPeriodLabel(monthBlock.month_key)} (Monto)</th>
                                                </React.Fragment>
                                              ))}
                                            </tr>
                                          </thead>
                                          <tbody className="bg-white divide-y divide-gray-200">
                                            {family.rows.map((row, idx) => (
                                              <tr key={idx} className="hover:bg-gray-50">
                                                <td className="px-3 py-2 text-sm font-medium text-gray-900">{row.product_name}</td>
                                                <td className="px-3 py-2 text-sm text-gray-500">{row.product_sku}</td>
                                                <td className="px-3 py-2 text-sm text-gray-500">{row.warehouse_name}</td>
                                                <td className="px-3 py-2">
                                                  <button
                                                    type="button"
                                                  onClick={() => openExitsDetailModal(row.product_id, row.warehouse_id, row.product_name, row.warehouse_name, block.apiSide)}
                                                    className="text-sm font-medium text-teal-600 hover:text-teal-800 underline"
                                                  >
                                                    Ver detalle
                                                  </button>
                                                </td>
                                              {byMonth.map((monthBlock) => {
                                                const val = row.months[monthBlock.month_key];
                                                  const qty = val ? val.quantity : 0;
                                                  const amt = val ? val.amount : 0;
                                                  return (
                                                  <React.Fragment key={monthBlock.month_key}>
                                                      <td className="px-3 py-2 text-sm text-right">
                                                        <span className={qty < 0 ? 'text-red-600 font-medium' : 'text-gray-900'}>
                                                        {formatoCantidad.format(Number(qty))}
                                                        </span>
                                                      </td>
                                                      <td className="px-3 py-2 text-sm text-right">
                                                        <span className={amt < 0 ? 'text-red-600 font-medium' : 'text-gray-900'}>
                                                        S/ {formatoMonto.format(Number(amt))}
                                                        </span>
                                                      </td>
                                                    </React.Fragment>
                                                  );
                                                })}
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                    </div>
                                  </div>
                                ))}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
              </div>
            )}
          </div>

          {/* Secci�n 2: Compra directa � por meses, monto sin IGV, misma l�gica que secci�n 1 */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-2">
              {block.apiSide === 'ventas' ? 'Venta directa' : 'Compra directa'}
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              {block.apiSide === 'ventas'
                ? 'Productos vendidos sin salida de almac�n en el per�odo (�rdenes de venta emitidas). Cantidad y monto sin IGV. Expanda una categor�a para ver sus subcategor�as, familias y productos. Haga clic en Ver detalle para ver las �rdenes por producto.'
                : '�tems de consumo directo (compras o suministros continuos) desglosados por mes. Cantidad y monto sin IGV. Mismo per�odo y filtro de proyecto. Expanda una categor�a para ver sus subcategor�as, familias y productos. Haga clic en Ver detalle en una fila para ver los documentos/�tems de ese producto.'}
            </p>

            {directCategories.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-500">
                No hay �tems de consumo directo en el per�odo seleccionado.
              </div>
            ) : (
              <div className="space-y-4">
                {directCategories.map((category) => {
                  const categoryTreeState = directTrees[productCategoryKey(block.apiSide, category)];
                  const categoryTree = categoryTreeState?.status === 'loaded' ? categoryTreeState.tree : [];
                  const isExpanded = expandedDirectCategories.has(category.id);
                  const monthAmounts = category.by_month || {};
                  return (
                  <div key={`s2-cat-${category.id}`} className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
                    <div
                      className="bg-teal-50 px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-teal-100 transition-colors"
                        onClick={() => handleToggleDirectCategory(category)}
                    >
                      <div className="flex items-center gap-3">
                          {categoryTreeState?.status === 'loading' ? (
                            <span className="inline-block w-5 h-5 border-2 border-teal-600 border-t-transparent rounded-full animate-spin" />
                          ) : (
                        <svg
                              className={`w-5 h-5 text-teal-600 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                          )}
                          <span className="font-semibold text-teal-800">{category.category_code || category.category_name}</span>
                          {category.category_code && <span className="text-gray-800 font-medium">{category.category_name}</span>}
                        <span className="text-xs text-gray-500">(Categor�a)</span>
                      </div>
                        <span className="text-sm text-gray-600">{category.subcategory_count} subcategor�a(s)</span>
                    </div>

                      <div className="border-t border-gray-200 overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              {byMonth.map((monthBlock) => (
                                <th key={monthBlock.month_key} className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                                  {getPeriodLabel(monthBlock)}
                                </th>
                              ))}
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="bg-white">
                              {byMonth.map((monthBlock) => {
                                const hasMonth = Object.prototype.hasOwnProperty.call(monthAmounts, monthBlock.month_key);
                                const amount = Number(monthAmounts[monthBlock.month_key] ?? 0);
                                return (
                                  <td key={monthBlock.month_key} className="px-3 py-2 text-sm text-right whitespace-nowrap">
                                    {hasMonth ? (
                                      <span className={amount < 0 ? 'text-red-600 font-medium' : 'text-gray-900'}>
                                        S/ {formatoMonto.format(amount)}
                                      </span>
                                    ) : (
                                      <span className="text-gray-300">�</span>
                                    )}
                                  </td>
                                );
                              })}
                              <td className="px-3 py-2 text-sm text-right whitespace-nowrap">
                                <span className={Number(category.total_amount ?? 0) < 0 ? 'text-red-600 font-semibold' : 'text-teal-900 font-semibold'}>
                                  S/ {formatoMonto.format(Number(category.total_amount ?? 0))}
                                </span>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      {isExpanded && (
                        <div className="border-t border-gray-200 bg-gray-50">
                                    {categoryTreeState?.status === 'loading' && (
                                      <div className="px-6 py-4 text-sm text-gray-500">Cargando detalle de la categor�a�</div>
                                    )}
                                    {categoryTreeState?.status === 'error' && (
                                      <div className="px-6 py-4 text-sm text-red-600">No se pudo cargar el detalle de la categor�a.</div>
                                    )}
                                    {categoryTreeState?.status === 'loaded' && categoryTree.length === 0 && (
                                      <div className="px-6 py-4 text-sm text-gray-500">Sin detalles para esta categor�a en el per�odo.</div>
                                    )}
                                    {categoryTree.flatMap((treeCategory) => treeCategory.subcategories.map((subcategory) => {
                          return (
                            <div key={`s2-sub-${subcategory.id}`} className="border-t border-gray-200 first:border-t-0">
                              <div className="bg-purple-50 px-6 py-2.5 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className="w-4 h-4 border-l-2 border-t-2 border-purple-300" />
                                  <span className="font-semibold text-purple-800">{subcategory.code || subcategory.name || '�'}</span>
                                  {subcategory.code && subcategory.name && <span className="text-gray-800">{subcategory.name}</span>}
                                  {!subcategory.code && !subcategory.name && <span className="text-gray-500">Sin subcategor�a</span>}
                                  <span className="text-xs text-gray-500">(Subcategor�a)</span>
                                </div>
                                <span className="text-sm text-gray-600">{subcategory.families.length} familia(s)</span>
                              </div>

                              {subcategory.families.length > 0 && (
                                <div className="bg-gray-50/50">
                                  {subcategory.families.map((family) => (
                                    <div key={`s2-fam-${family.id}`} className="border-t border-gray-200 first:border-t-0">
                                      <div className="px-8 py-2 flex items-center bg-gray-100/80 border-b border-gray-200">
                                        <div className="w-4 h-4 border-l-2 border-t-2 border-gray-400" />
                                        <span className="font-semibold text-gray-800 ml-2">{family.code || family.name || '�'}</span>
                                        {family.code && family.name && <span className="text-gray-700 ml-1">{family.name}</span>}
                                        {!family.code && !family.name && <span className="text-gray-500 ml-1">Sin familia</span>}
                                        <span className="text-xs text-gray-500 ml-2">(Familia) � {family.rows.length} producto(s)</span>
                                      </div>
                                      <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                          <thead className="bg-gray-100">
                                            <tr>
                                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Producto</th>
                                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-24">Detalle</th>
                                              {byMonth.map((block) => (
                                                <React.Fragment key={block.month_key}>
                                                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{getPeriodLabel(block)} (Cant.)</th>
                                                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{getPeriodLabel(block)} (Monto sin IGV)</th>
                                                </React.Fragment>
                                              ))}
                                            </tr>
                                          </thead>
                                          <tbody className="bg-white divide-y divide-gray-200">
                                            {family.rows.map((row, idx) => (
                                              <tr key={idx} className="hover:bg-gray-50">
                                                <td className="px-3 py-2 text-sm font-medium text-gray-900">{row.product_name}</td>
                                                <td className="px-3 py-2 text-sm text-gray-500">{row.product_sku}</td>
                                                <td className="px-3 py-2">
                                                  <button
                                                    type="button"
                                                    onClick={() => openComprasDetailModal(row.product_id, row.product_name, block.apiSide)}
                                                    className="text-sm font-medium text-teal-600 hover:text-teal-800 underline"
                                                  >
                                                    Ver detalle
                                                  </button>
                                                </td>
                                                {byMonth.map((block) => {
                                                  const val = row.months[block.month_key];
                                                  const qty = val ? val.quantity : 0;
                                                  const amt = val ? val.amount : 0;
                                                  return (
                                                    <React.Fragment key={block.month_key}>
                                                      <td className="px-3 py-2 text-sm text-right">
                                                        <span className={qty < 0 ? 'text-red-600 font-medium' : 'text-gray-900'}>
                                                          {formatoCantidad.format(Number(qty))}
                                                        </span>
                                                      </td>
                                                      <td className="px-3 py-2 text-sm text-right">
                                                        <span className={amt < 0 ? 'text-red-600 font-medium' : 'text-gray-900'}>
                                                          S/ {formatoMonto.format(Number(amt))}
                                                        </span>
                                                      </td>
                                                    </React.Fragment>
                                                  );
                                                })}
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                              </div>
                            );
                          }))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

          {block.showDep && (
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-2">
                Depreciaciones
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                Depreciaci�n de activos fijos en el per�odo seleccionado (montos sin IGV), agrupada por clasificaci�n de activos. Expanda una categor�a para ver sus subcategor�as, familias y activos.
              </p>

              {depCategories.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-500">
                  No hay depreciaciones en el per�odo seleccionado.
                </div>
              ) : (
                <div className="space-y-4">
                  {depCategories.map((category) => {
                    const categoryTreeState = depTrees[depCategoryKey(category)];
                    const categoryTree = categoryTreeState?.status === 'loaded' ? categoryTreeState.tree : [];
                    const isExpanded = expandedDepCategories.has(category.id);
                    const monthAmounts = category.by_month || {};
                    return (
                      <div key={`dep-cat-${category.id}`} className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
                        <div
                          className="bg-teal-50 px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-teal-100 transition-colors"
                          onClick={() => handleToggleDepCategory(category)}
                        >
                          <div className="flex items-center gap-3">
                            {categoryTreeState?.status === 'loading' ? (
                              <span className="inline-block w-5 h-5 border-2 border-teal-600 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <svg
                                className={`w-5 h-5 text-teal-600 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            )}
                            <span className="font-semibold text-teal-800">{category.category_code || category.category_name}</span>
                            {category.category_code && <span className="text-gray-800 font-medium">{category.category_name}</span>}
                            <span className="text-xs text-gray-500">(Categor�a)</span>
                          </div>
                          <span className="text-sm text-gray-600">{category.subcategory_count} subcategor�a(s)</span>
                        </div>

                        <div className="border-t border-gray-200 overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                {depreciationPeriods.map((monthBlock) => (
                                  <th key={monthBlock.month_key} className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                                    {getPeriodLabel(monthBlock)}
                                  </th>
                                ))}
                                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr className="bg-white">
                                {depreciationPeriods.map((monthBlock) => {
                                  const hasMonth = Object.prototype.hasOwnProperty.call(monthAmounts, monthBlock.month_key);
                                  const amount = Number(monthAmounts[monthBlock.month_key] ?? 0);
                                  return (
                                    <td key={monthBlock.month_key} className="px-3 py-2 text-sm text-right whitespace-nowrap">
                                      {hasMonth ? (
                                        <span className={amount < 0 ? 'text-red-600 font-medium' : 'text-gray-900'}>
                                          S/ {formatoMonto.format(amount)}
                                        </span>
                                      ) : (
                                        <span className="text-gray-300">�</span>
                                      )}
                                    </td>
                                  );
                                })}
                                <td className="px-3 py-2 text-sm text-right whitespace-nowrap">
                                  <span className={Number(category.total_amount ?? 0) < 0 ? 'text-red-600 font-semibold' : 'text-teal-900 font-semibold'}>
                                    S/ {formatoMonto.format(Number(category.total_amount ?? 0))}
                                  </span>
                                </td>
                              </tr>
                            </tbody>
                          </table>
              </div>

                        {isExpanded && (
                          <div className="border-t border-gray-200 bg-gray-50">
                                      {categoryTreeState?.status === 'loading' && (
                                        <div className="px-6 py-4 text-sm text-gray-500">Cargando detalle de la categor�a�</div>
                                      )}
                                      {categoryTreeState?.status === 'error' && (
                                        <div className="px-6 py-4 text-sm text-red-600">No se pudo cargar el detalle de la categor�a.</div>
                                      )}
                                      {categoryTreeState?.status === 'loaded' && categoryTree.length === 0 && (
                                        <div className="px-6 py-4 text-sm text-gray-500">Sin detalles para esta categor�a en el per�odo.</div>
                                      )}
                                      {categoryTree.flatMap((treeCategory) => treeCategory.subcategories.map((subcategory) => {
                                        return (
                              <div key={`dep-sub-${subcategory.id}`} className="border-t border-gray-200 first:border-t-0">
                                <div className="bg-purple-50 px-6 py-2.5 flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <div className="w-4 h-4 border-l-2 border-t-2 border-purple-300" />
                                    <span className="font-semibold text-purple-800">{subcategory.code || subcategory.name || '�'}</span>
                                    {subcategory.code && subcategory.name && <span className="text-gray-800">{subcategory.name}</span>}
                                    {!subcategory.code && !subcategory.name && <span className="text-gray-500">Sin subcategor�a</span>}
                                    <span className="text-xs text-gray-500">(Subcategor�a)</span>
                                  </div>
                                  <span className="text-sm text-gray-600">
                                    {subcategory.families.length} familia(s)
                                  </span>
          </div>

                                {subcategory.families.length > 0 && (
                                  <div className="bg-gray-50/50">
                                    {subcategory.families.map((family) => (
                                      <div key={`dep-fam-${family.id}`} className="border-t border-gray-200 first:border-t-0">
                                        <div className="px-8 py-2 flex items-center bg-gray-100/80 border-b border-gray-200">
                                          <div className="w-4 h-4 border-l-2 border-t-2 border-gray-400" />
                                          <span className="font-semibold text-gray-800 ml-2">{family.code || family.name || '�'}</span>
                                          {family.code && family.name && <span className="text-gray-700 ml-1">{family.name}</span>}
                                          {!family.code && !family.name && <span className="text-gray-500 ml-1">Sin familia</span>}
                                          <span className="text-xs text-gray-500 ml-2">(Familia) � {family.rows.length} activo(s)</span>
                                        </div>
                                        <div className="overflow-x-auto">
                                          <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-100">
                                              <tr>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">C�digo</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Activo</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">�rea</th>
                                                {depreciationPeriods.map((block) => (
                                                  <th key={block.month_key} className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                                                    {getPeriodLabel(block)} (Depreciaci�n)
                                                  </th>
                                                ))}
                                              </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                              {family.rows.map((row) => (
                                                <tr key={row.activo_id} className="hover:bg-gray-50">
                                                  <td className="px-3 py-2 text-sm font-medium text-gray-900">{row.code}</td>
                                                  <td className="px-3 py-2 text-sm text-gray-900">{row.name}</td>
                                                  <td className="px-3 py-2 text-sm text-gray-600">{row.area_name || 'Sin �rea'}</td>
                                                  {depreciationPeriods.map((block) => {
                                                    const amt = row.months?.[block.month_key] != null ? Number(row.months[block.month_key]) : 0;
                                                    return (
                                                      <td key={block.month_key} className="px-3 py-2 text-sm text-right font-medium">
                                                        S/ {formatoMonto.format(amt)}
                                                      </td>
                                                    );
                                                  })}
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                </div>
                              );
                            }))}
                                  </div>
                            )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Secci�n 3: Servicios � totales por clasificaci�n (solo servicios continuos, sin documentos) y opcional detalle */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-2">
              Servicios
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              {block.apiSide === 'ventas'
                ? 'Totales por clasificaci�n (servicios continuos de ventas e �tems de �rdenes de servicio de venta, prorrateados por fechas de servicio). Ver detalle para el desglose.'
                : 'Totales por clasificaci�n de servicio (servicios continuos y documentos). Haga clic en Ver detalle para ver los servicios y documentos que componen cada total.'}
            </p>

            {(!blockReport?.servicios || blockReport.servicios.length === 0) ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-500">
                No hay servicios en el per�odo seleccionado.
              </div>
            ) : (
              <>
                {/* Totales por clasificaci�n: todas las filas (servicios continuos + documentos), agrupadas por service_name */}
                {(() => {
                  const todasLasFilas = blockReport?.servicios || [];
                  const porClasificacion = {};
                  todasLasFilas.forEach((row) => {
                    const clave = row.service_name && String(row.service_name).trim() ? row.service_name : 'Sin clasificaci�n';
                    if (!porClasificacion[clave]) {
                      porClasificacion[clave] = { service_name: clave, months: {} };
                    }
                    byMonth.forEach((block) => {
                      const val = getDocumentProratedAmount(row, block);
                      porClasificacion[clave].months[block.month_key] = (porClasificacion[clave].months[block.month_key] || 0) + val;
                    });
                  });
                  const totalesRows = Object.values(porClasificacion).sort((a, b) => (a.service_name || '').localeCompare(b.service_name || ''));
                  if (totalesRows.length === 0) {
                    return (
                      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center text-gray-500">
                        No hay servicios en el per�odo.
                      </div>
                    );
                  }
                  return (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-4">
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-teal-50">
                            <tr>
                              <th className="px-3 py-3 text-left text-xs font-medium text-teal-800 uppercase">Clasificaci�n del servicio</th>
                              <th className="px-3 py-3 text-left text-xs font-medium text-teal-800 uppercase w-24">Detalle</th>
                              {byMonth.map((block) => (
                                <th key={block.month_key} className="px-3 py-3 text-right text-xs font-medium text-teal-800 uppercase whitespace-nowrap">
                                  {getPeriodLabel(block)} (Monto sin IGV)
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {totalesRows.map((row, idx) => (
                              <tr key={idx} className="hover:bg-gray-50">
                                <td className="px-3 py-3 text-sm font-medium text-gray-900">{row.service_name}</td>
                                <td className="px-3 py-3">
                                  <button
                                    type="button"
                                    onClick={() => openServiciosDetailModal(row.service_name, block.apiSide)}
                                    className="text-sm font-medium text-teal-600 hover:text-teal-800 underline"
                                  >
                                    Ver detalle
                                  </button>
                                </td>
                                {byMonth.map((block) => {
                                  const amt = row.months && row.months[block.month_key] != null ? Number(row.months[block.month_key]) : 0;
                                  return (
                                    <td key={block.month_key} className="px-3 py-3 text-sm text-right font-medium">
                                      <span className={amt < 0 ? 'text-red-600' : 'text-gray-900'}>
                                        S/ {formatoMonto.format(amt)}
                                      </span>
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })()}
              </>
            )}
          </div>
              </div>
            );
          })}
        </>
      )}

      {/* Modal: Detalle de salidas (productos de almac�n) */}
      {showExitsDetailModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={closeExitsDetailModal}>
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">
                {exitsDetailProduct
                  ? `Detalle de salidas: ${exitsDetailProduct.product_name} � ${exitsDetailProduct.warehouse_name}`
                  : 'Detalle de salidas (productos de almac�n)'}
              </h3>
              <button
                type="button"
                onClick={closeExitsDetailModal}
                className="p-1 rounded text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                aria-label="Cerrar"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="px-4 py-2 text-sm text-gray-600 border-b border-gray-100">
              {(exitsDetail?.date_from && exitsDetail?.date_to) || (reportData?.date_from && reportData?.date_to) ? (
                <span>Per�odo: {formatDate(exitsDetail?.date_from || reportData?.date_from)} � {formatDate(exitsDetail?.date_to || reportData?.date_to)}</span>
              ) : null}
            </div>
            <div className="flex-1 overflow-auto p-4">
              {exitsDetailLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-teal-600 border-t-transparent" />
                </div>
              ) : exitsDetail?.exits?.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No hay salidas en el per�odo seleccionado.</p>
              ) : exitsDetail?.exits?.length > 0 ? (
                (() => {
                  let periods = reportPeriods;
                  const milestonePeriods = reportData?.milestone_periods || [];
                  const useMilestonePeriods = milestonePeriods.length > 0 && periods.some((b) => String(b.month_key || '').startsWith('milestone_'));
                  if (periods.length === 0 && exitsDetail.exits.length > 0 && !useMilestonePeriods) {
                    const monthKeys = [...new Set(exitsDetail.exits.map((row) => row.movement_date && String(row.movement_date).length >= 7 ? String(row.movement_date).substring(0, 7) : null).filter(Boolean))].sort();
                    periods = monthKeys.map((key) => ({ month_key: key }));
                  }
                  const exitsByMonth = {};
                  const assignKey = (movementDate) => {
                    if (!movementDate || String(movementDate).length < 10) return null;
                    const d = String(movementDate).substring(0, 10);
                    if (useMilestonePeriods && milestonePeriods.length > 0) {
                      const found = milestonePeriods.find((p) => d >= p.date_from && d <= p.date_to);
                      return found ? found.key : null;
                    }
                    return String(movementDate).length >= 7 ? String(movementDate).substring(0, 7) : null;
                  };
                  exitsDetail.exits.forEach((row) => {
                    const periodKey = assignKey(row.movement_date);
                    if (periodKey) {
                      if (!exitsByMonth[periodKey]) exitsByMonth[periodKey] = [];
                      exitsByMonth[periodKey].push(row);
                    }
                  });
                  return (
                    <div className="space-y-6">
                      {periods.map((block) => {
                        const monthExits = exitsByMonth[block.month_key] || [];
                        return (
                          <div key={block.month_key} className="border border-gray-200 rounded-lg overflow-hidden">
                            <div className="bg-teal-50 px-4 py-2 border-b border-gray-200">
                              <h4 className="text-sm font-semibold text-teal-800">{getPeriodLabel(block)}</h4>
                            </div>
                            {monthExits.length === 0 ? (
                              <div className="px-4 py-4 text-sm text-gray-500 text-center">No hay movimientos en este per�odo.</div>
                            ) : (
                              (() => {
                                const totalQty = monthExits.reduce((s, r) => s + Number(r.quantity ?? 0), 0);
                                const totalAmt = monthExits.reduce((s, r) => s + Number(r.total ?? 0), 0);
                                const showVehicleColumn = monthExits.some((r) => {
                                  const label = r?.vehicle_label;
                                  return label != null && String(label).trim() !== '';
                                });
                                return (
                              <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                  <thead className="bg-gray-100">
                                    <tr>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Hora</th>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Gu�a</th>
                                      {!exitsDetailProduct && (
                                        <>
                                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Producto</th>
                                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Almac�n</th>
                                        </>
                                      )}
                                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                                      {showVehicleColumn && (
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Veh�culo / Maquinaria</th>
                                      )}
                                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Cantidad</th>
                                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Costo unit.</th>
                                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Monto (S/)</th>
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white divide-y divide-gray-200">
                                    {monthExits.map((row) => (
                                      <tr key={row.id} className="hover:bg-gray-50">
                                        <td className="px-3 py-2 text-sm text-gray-900">{formatDate(row.movement_date)}</td>
                                        <td className="px-3 py-2 text-sm text-gray-600">{row.movement_time || '�'}</td>
                                        <td className="px-3 py-2 text-sm text-gray-700 font-medium">{row.entry_guide_code || '�'}</td>
                                        {!exitsDetailProduct && (
                                          <>
                                            <td className="px-3 py-2 text-sm font-medium text-gray-900">{row.product_name}</td>
                                            <td className="px-3 py-2 text-sm text-gray-500">{row.product_sku}</td>
                                            <td className="px-3 py-2 text-sm text-gray-600">{row.warehouse_name}</td>
                                          </>
                                        )}
                                        <td className="px-3 py-2 text-sm text-gray-600">{row.tipo_label}</td>
                                        {showVehicleColumn && (
                                          <td className="px-3 py-2 text-sm text-gray-600">{row.vehicle_label || '�'}</td>
                                        )}
                                        <td className={`px-3 py-2 text-sm text-right font-medium ${row.quantity < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                                          {formatoCantidad.format(row.quantity)}
                                        </td>
                                        <td className="px-3 py-2 text-sm text-right text-gray-700">
                                          S/ {formatoMonto.format((row.unit_price ?? 0))}
                                        </td>
                                        <td className={`px-3 py-2 text-sm text-right font-medium ${row.total < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                                          S/ {formatoMonto.format(row.total)}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                  <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                                    <tr>
                                      <td colSpan={exitsDetailProduct ? (showVehicleColumn ? 5 : 4) : (showVehicleColumn ? 8 : 7)} className="px-3 py-2 text-sm font-semibold text-gray-800">Total per�odo</td>
                                      <td className="px-3 py-2 text-sm text-right font-semibold text-gray-800">
                                        {formatoCantidad.format(totalQty)}
                                      </td>
                                      <td className="px-3 py-2 text-sm text-right text-gray-500">�</td>
                                      <td className={`px-3 py-2 text-sm text-right font-semibold ${totalAmt < 0 ? 'text-red-600' : 'text-gray-800'}`}>
                                        S/ {formatoMonto.format(totalAmt)}
                                      </td>
                                    </tr>
                                  </tfoot>
                                </table>
                              </div>
                                );
                              })()
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Modal: Detalle de compra directa */}
      {showComprasDetailModal && comprasDetailProduct && modalActiveReport?.compras_sin_almacen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={closeComprasDetailModal}>
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">
                Detalle: {comprasDetailProduct.product_name}
              </h3>
              <button
                type="button"
                onClick={closeComprasDetailModal}
                className="p-1 rounded text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                aria-label="Cerrar"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="px-4 py-2 text-sm text-gray-600 border-b border-gray-100">
              {reportData?.date_from && reportData?.date_to && (
                <span>Per�odo: {formatDate(reportData.date_from)} � {formatDate(reportData.date_to)}. Lista por per�odo solicitado (fecha de ingreso para suministros continuos).</span>
              )}
            </div>
            <div className="flex-1 overflow-auto p-4">
              {(() => {
                const items = modalActiveReport.compras_sin_almacen.filter((i) => Number(i.product_id) === Number(comprasDetailProduct.product_id));
                if (items.length === 0) {
                  return <p className="text-center text-gray-500 py-8">No hay �tems de consumo directo para este producto en el per�odo.</p>;
                }
                let periods = reportPeriods;
                const milestonePeriods = reportData?.milestone_periods || [];
                const useMilestonePeriods = milestonePeriods.length > 0 && periods.some((b) => String(b.month_key || '').startsWith('milestone_'));
                if (periods.length === 0 && !useMilestonePeriods) {
                  const monthKeys = [...new Set(items.map((i) => i.issue_date && String(i.issue_date).length >= 7 ? String(i.issue_date).substring(0, 7) : null).filter(Boolean))].sort();
                  periods = monthKeys.map((key) => ({ month_key: key }));
                }
                const assignPeriodKey = (dateStr) => {
                  if (!dateStr || String(dateStr).length < 10) return String(dateStr).length >= 7 ? String(dateStr).substring(0, 7) : null;
                  const d = String(dateStr).substring(0, 10);
                  if (useMilestonePeriods && milestonePeriods.length > 0) {
                    const found = milestonePeriods.find((p) => d >= p.date_from && d <= p.date_to);
                    return found ? found.key : null;
                  }
                  return String(dateStr).length >= 7 ? String(dateStr).substring(0, 7) : null;
                };
                const itemsByMonth = {};
                items.forEach((item) => {
                  const periodKey = assignPeriodKey(item.issue_date);
                  if (periodKey) {
                    if (!itemsByMonth[periodKey]) itemsByMonth[periodKey] = [];
                    itemsByMonth[periodKey].push(item);
                  }
                });
                const onlySuministros = items.length > 0 && items.every((i) => i.document_type === 'suministro_continuo');
                const tipoDocLabel = (t) => (t === 'suministro_continuo' ? 'Suministro continuo' : t === 'factura' ? 'Factura' : t === 'boleta' ? 'Boleta' : t === 'recibo_por_honorarios' ? 'Recibo por Honorarios' : t === 'nota_credito' ? 'Nota de cr�dito' : t === 'nota_debito' ? 'Nota de d�bito' : t === 'otro' ? 'Otro' : t ? String(t) : '�');
                const refDocLabel = (item) => {
                  const ref = item.reference_document_type || item.document_type;
                  if (!ref) return null;
                  return ref === 'factura' ? 'Factura' : ref === 'boleta' ? 'Boleta' : ref === 'recibo_por_honorarios' ? 'Recibo por Honorarios' : ref === 'nota_credito' ? 'Nota de cr�dito' : ref === 'nota_debito' ? 'Nota de d�bito' : ref === 'otro' ? 'Otro' : ref === 'suministro_continuo' ? 'Suministro continuo' : ref;
                };
                return (
                  <div className="space-y-6">
                    {periods.map((block) => {
                      const monthItems = (itemsByMonth[block.month_key] || []).slice().sort((a, b) => {
                        const da = a.issue_date || '';
                        const db = b.issue_date || '';
                        return String(da).localeCompare(String(db));
                      });
                      const showVehicleColumn = monthItems.some((item) => {
                        const label = item?.vehicle_label;
                        return label != null && String(label).trim() !== '';
                      });
                      const totalQty = monthItems.reduce((s, r) => s + Number(r.quantity ?? 0), 0);
                      const totalAmt = monthItems.reduce((s, r) => s + Number(r.subtotal ?? r.total ?? 0), 0);
                      return (
                        <div key={block.month_key} className="border border-gray-200 rounded-lg overflow-hidden">
                          <div className="bg-teal-50 px-4 py-2 border-b border-gray-200 flex items-center justify-between flex-wrap gap-1">
                            <h4 className="text-sm font-semibold text-teal-800">{getPeriodLabel(block)}</h4>
                            {onlySuministros || monthItems.every((i) => i.document_type === 'suministro_continuo') ? (
                              <span className="text-xs font-medium text-teal-700 bg-teal-100 px-2 py-0.5 rounded">Suministros continuos</span>
                            ) : null}
                          </div>
                          {monthItems.length === 0 ? (
                            <div className="px-4 py-4 text-sm text-gray-500 text-center">No hay movimientos en este per�odo.</div>
                          ) : (
                            <div className="overflow-x-auto">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-100">
                                  <tr>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Documento</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Proveedor</th>
                                    {showVehicleColumn && (
                                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Veh�culo / Maquinaria</th>
                                    )}
                                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Cantidad</th>
                                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Costo unit.</th>
                                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Monto sin IGV</th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {monthItems.map((item) => (
                                    <tr key={item.document_item_id} className="hover:bg-gray-50">
                                      <td className="px-3 py-2 text-sm text-gray-900">{formatDate(item.issue_date)}</td>
                                      <td className="px-3 py-2 text-sm font-medium text-gray-700">
                                        {item.document_code || '�'}
                                        {refDocLabel(item) ? ` (${refDocLabel(item)})` : ''}
                                        {(item.document_series || item.document_number) && (
                                          <span className="block text-gray-500 font-normal mt-0.5">
                                            {item.document_series && item.document_number
                                              ? `Serie ${item.document_series}, N� ${item.document_number}`
                                              : item.document_series
                                                ? `Serie ${item.document_series}`
                                                : `N� ${item.document_number}`}
                                          </span>
                                        )}
                                      </td>
                                      <td className="px-3 py-2 text-sm text-gray-600">{tipoDocLabel(item.document_type)}</td>
                                      <td className="px-3 py-2 text-sm text-gray-600">{item.supplier_name || '�'}</td>
                                      {showVehicleColumn && (
                                        <td className="px-3 py-2 text-sm text-gray-600">{item.vehicle_label || '�'}</td>
                                      )}
                                      <td className="px-3 py-2 text-sm text-right text-gray-900">
                                        {formatoCantidad.format(Number(item.quantity ?? 0))}
                                      </td>
                                      <td className="px-3 py-2 text-sm text-right text-gray-700">
                                        S/ {formatoMonto.format(Number(item.unit_price ?? 0))}
                                      </td>
                                      <td className="px-3 py-2 text-sm text-right font-medium text-gray-900">
                                        S/ {formatoMonto.format(Number(item.subtotal ?? item.total ?? 0))}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                                <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                                  <tr>
                                    <td colSpan={showVehicleColumn ? 5 : 4} className="px-3 py-2 text-sm font-semibold text-gray-800">Total per�odo</td>
                                    <td className="px-3 py-2 text-sm text-right font-semibold text-gray-800">
                                      {formatoCantidad.format(totalQty)}
                                    </td>
                                    <td className="px-3 py-2 text-sm text-right text-gray-500">�</td>
                                    <td className="px-3 py-2 text-sm text-right font-semibold text-gray-800">
                                      S/ {formatoMonto.format(totalAmt)}
                                    </td>
                                  </tr>
                                </tfoot>
                              </table>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Modal: Detalle de servicios por clasificaci�n */}
      {showServiciosDetailModal && serviciosDetailClassification != null && modalActiveReport?.servicios && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={closeServiciosDetailModal}>
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">
                Detalle: {serviciosDetailClassification}
              </h3>
              <button
                type="button"
                onClick={closeServiciosDetailModal}
                className="p-1 rounded text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                aria-label="Cerrar"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="px-4 py-2 text-sm text-gray-600 border-b border-gray-100">
              {reportData?.date_from && reportData?.date_to && (
                <span>Per�odo: {formatDate(reportData.date_from)} � {formatDate(reportData.date_to)}</span>
              )}
            </div>
            <div className="flex-1 overflow-auto p-4">
              {(() => {
                const detailRows = modalActiveReport.servicios.filter(
                  (r) => (r.service_name && String(r.service_name).trim() ? r.service_name : 'Sin clasificaci�n') === serviciosDetailClassification
                );
                if (detailRows.length === 0) {
                  return <p className="text-center text-gray-500 py-8">No hay detalle para esta clasificaci�n.</p>;
                }
                let monthBlocks = reportPeriods;
                if (monthBlocks.length === 0 && detailRows[0]?.months) {
                  const keys = Object.keys(detailRows[0].months).sort();
                  monthBlocks = keys.map((k) => ({ month_key: k }));
                }
                return (
                  <div className="space-y-6">
                    {monthBlocks.map((block) => {
                      const monthKey = block.month_key;
                      const rowsInMonth = detailRows.filter((row) => {
                        const amt = getDocumentProratedAmount(row, block);
                        return amt !== 0;
                      });
                      const showVehicleColumn = rowsInMonth.some((row) => {
                        const label = row?.vehicle_label;
                        return label != null && String(label).trim() !== '';
                      });
                      const totalMonth = detailRows.reduce((s, row) => s + getDocumentProratedAmount(row, block), 0);
                      return (
                        <div key={monthKey} className="border border-gray-200 rounded-lg overflow-hidden">
                          <div className="bg-teal-50 px-4 py-2 border-b border-gray-200">
                            <h4 className="text-sm font-semibold text-teal-800">{getPeriodLabel(monthKey)}</h4>
                          </div>
                          {rowsInMonth.length === 0 ? (
                            <div className="px-4 py-4 text-sm text-gray-500 text-center">No hay movimientos en este per�odo.</div>
                          ) : (
                            <div className="overflow-x-auto">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-100">
                                  <tr>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Origen</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Servicio / Descripci�n</th>
                                    {showVehicleColumn && (
                                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Veh�culo / Maquinaria</th>
                                    )}
                                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Monto sin IGV</th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {rowsInMonth.map((row, idx) => {
                                    const amt = getDocumentProratedAmount(row, block);
                                    return (
                                      <tr key={`${row.source_type}-${row.source_id}-${monthKey}-${idx}`} className="hover:bg-gray-50">
                                        <td className="px-3 py-2 text-sm text-gray-600">
                                          {row.source_type === 'continuous_service' ? 'Servicio continuo' : 'Documento'}
                                        </td>
                                        <td className="px-3 py-2 text-sm text-gray-600">
                                          {row.source_type === 'continuous_service' ? (row.status === 'open' ? 'Abierto' : 'Cerrado') : '�'}
                                        </td>
                                        <td className="px-3 py-2 text-sm font-medium text-gray-900">
                                          {row.source_type === 'continuous_service' ? (
                                            <button
                                              type="button"
                                              onClick={() => openCSDetailModal(row.source_id, monthKey)}
                                              className="text-teal-600 hover:text-teal-800 hover:underline text-left"
                                            >
                                              {row.label}
                                            </button>
                                          ) : (
                                            <div className="flex flex-col items-start gap-1">
                                              <span>{row.label}</span>
                                              {(row.document_series || row.document_number) && (
                                                <span className="text-gray-500 font-normal">
                                                  {row.document_series && row.document_number
                                                    ? `Serie ${row.document_series}, N� ${row.document_number}`
                                                    : row.document_series
                                                      ? `Serie ${row.document_series}`
                                                      : `N� ${row.document_number}`}
                                                </span>
                                              )}
                                              {row.service_start_date && row.service_end_date && (
                                                <button
                                                  type="button"
                                                  onClick={() => openProrrateoModal(row)}
                                                  className="text-teal-600 hover:text-teal-800 hover:underline text-sm font-normal"
                                                >
                                                  Ver f�rmula de prorrateo
                                                </button>
                                              )}
                                            </div>
                                          )}
                                        </td>
                                        {showVehicleColumn && (
                                          <td className="px-3 py-2 text-sm text-gray-600">
                                            {row.vehicle_label || '�'}
                                          </td>
                                        )}
                                        <td className="px-3 py-2 text-sm text-right">
                                          <span className={amt < 0 ? 'text-red-600 font-medium' : 'text-gray-900'}>
                                            S/ {formatoMonto.format(amt)}
                                          </span>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                                <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                                  <tr>
                                    <td colSpan={showVehicleColumn ? 4 : 3} className="px-3 py-2 text-sm font-semibold text-gray-800">Total per�odo</td>
                                    <td className={`px-3 py-2 text-sm text-right font-semibold ${totalMonth < 0 ? 'text-red-600' : 'text-gray-800'}`}>
                                      S/ {formatoMonto.format(totalMonth)}
                                    </td>
                                  </tr>
                                </tfoot>
                              </table>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Modal: Detalle de un servicio continuo (lista de avances) */}
      {showCSDetailModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50" onClick={closeCSDetailModal}>
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">
                {csDetailData ? `Servicio continuo: ${csDetailData.code || `SC-${csDetailId}`}` : 'Servicio continuo'}
              </h3>
              <button
                type="button"
                onClick={closeCSDetailModal}
                className="p-1 rounded text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                aria-label="Cerrar"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              {csDetailLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-teal-600 border-t-transparent" />
                </div>
              ) : !csDetailData ? (
                <p className="text-center text-gray-500 py-8">No se pudo cargar el servicio.</p>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                    <div><span className="text-gray-500">Proveedor:</span> <span className="font-medium">{csDetailData.supplier?.name || '�'}</span></div>
                    <div><span className="text-gray-500">Estado:</span> <span className="font-medium">{csDetailData.status === 'open' ? 'Abierto' : 'Cerrado'}</span></div>
                    <div><span className="text-gray-500">Fecha inicio:</span> <span className="font-medium">{formatDate(csDetailData.start_date)}</span></div>
                    <div><span className="text-gray-500">Fecha fin:</span> <span className="font-medium">{formatDate(csDetailData.end_date)}</span></div>
                  </div>
                  {csDetailPeriod && (
                    <p className="text-sm text-teal-700 font-medium mb-2">
                      Mostrando solo registros de {getPeriodLabel(csDetailPeriod)}
                    </p>
                  )}
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Registros de avance</h4>
                  {(() => {
                    const allProgress = csDetailData.progress || [];
                    const milestonePeriods = reportData?.milestone_periods || [];
                    const milestone = milestonePeriods.find((p) => p.key === csDetailPeriod);
                    const progressFiltered = csDetailPeriod
                      ? milestone
                        ? allProgress.filter((p) => {
                            const d = p.progress_date ? String(p.progress_date).substring(0, 10) : '';
                            return d >= milestone.date_from && d <= milestone.date_to;
                          })
                        : allProgress.filter((p) => {
                            const d = p.progress_date ? String(p.progress_date).substring(0, 7) : '';
                            return d === csDetailPeriod;
                          })
                      : allProgress;
                    if (progressFiltered.length === 0) {
                      return <p className="text-gray-500 py-4">{csDetailPeriod ? 'No hay registros de avance en este per�odo.' : 'No hay registros de avance.'}</p>;
                    }
                    const sorted = progressFiltered.slice().sort((a, b) => String(a.progress_date || '').localeCompare(String(b.progress_date || '')));
                    const totalSub = sorted.reduce((s, p) => s + Number(p.subtotal ?? (p.quantity * p.unit_price) ?? 0), 0);
                    return (
                      <div className="overflow-x-auto border border-gray-200 rounded-lg">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Servicio</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Descripci�n</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Cantidad</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Precio unit.</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Subtotal (sin IGV)</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {sorted.map((item) => (
                              <tr key={item.id} className="hover:bg-gray-50">
                                <td className="px-3 py-2 text-sm text-gray-900">{formatDate(item.progress_date)}</td>
                                <td className="px-3 py-2 text-sm text-gray-700">{item.service?.name || '�'}</td>
                                <td className="px-3 py-2 text-sm text-gray-600">{item.description || '�'}</td>
                                <td className="px-3 py-2 text-sm text-right text-gray-900">
                                  {formatoCantidad.format(Number(item.quantity ?? 0))}
                                </td>
                                <td className="px-3 py-2 text-sm text-right text-gray-700">
                                  S/ {formatoMonto.format(Number(item.unit_price ?? 0))}
                                </td>
                                <td className="px-3 py-2 text-sm text-right font-medium text-gray-900">
                                  S/ {formatoMonto.format(Number(item.subtotal ?? (item.quantity * item.unit_price) ?? 0))}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                            <tr>
                              <td colSpan={5} className="px-3 py-2 text-sm font-semibold text-gray-800">Total</td>
                              <td className="px-3 py-2 text-sm text-right font-semibold text-gray-800">
                                S/ {formatoMonto.format(totalSub)}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    );
                  })()}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal: F�rmula de prorrateo (documentos) */}
      {showProrrateoModal && prorrateoDocRow && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/50" onClick={closeProrrateoModal}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">
                F�rmula de prorrateo � {prorrateoDocRow.label || 'Documento'}
              </h3>
              <button
                type="button"
                onClick={closeProrrateoModal}
                className="p-1 rounded text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                aria-label="Cerrar"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4 space-y-4">
              <p className="text-sm text-gray-700">
                El monto total del documento se distribuye proporcionalmente seg�n los d�as entre la fecha de inicio y fin del servicio.
                Para cada mes del reporte: <strong>(Monto total � D�as del documento en el mes) � Total d�as del documento = Monto del mes.</strong>
              </p>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-gray-500">Fecha inicio:</span> <span className="font-medium">{formatDate(prorrateoDocRow.service_start_date)}</span></div>
                <div><span className="text-gray-500">Fecha fin:</span> <span className="font-medium">{formatDate(prorrateoDocRow.service_end_date)}</span></div>
                <div><span className="text-gray-500">Total d�as del documento:</span> <span className="font-medium">{totalDaysInRange(prorrateoDocRow.service_start_date, prorrateoDocRow.service_end_date)}</span></div>
                <div><span className="text-gray-500">Monto total (sin IGV):</span> <span className="font-medium">S/ {formatoMonto.format(Number(prorrateoDocRow.total_amount ?? 0))}</span></div>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Desglose por per�odo</h4>
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Mes</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">D�as en el per�odo</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">F�rmula con n�meros</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Monto (S/)</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {(() => {
                        const totalDocDays = totalDaysInRange(prorrateoDocRow.service_start_date, prorrateoDocRow.service_end_date);
                        const totalAmount = Number(prorrateoDocRow.total_amount ?? 0);
                        const fmt = (n) => formatoMontoVariable.format(n);
                        return reportPeriods.map((block) => {
                          const days = block.date_from && block.date_to
                            ? overlapDaysInPeriod(prorrateoDocRow.service_start_date, prorrateoDocRow.service_end_date, block.date_from, block.date_to)
                            : 0;
                          const amt = getDocumentProratedAmount(prorrateoDocRow, block);
                          if (days === 0 && amt === 0) return null;
                          const formula = totalDocDays > 0
                            ? `(S/ ${fmt(totalAmount)} � ${days}) � ${totalDocDays} = S/ ${fmt(amt)}`
                            : '�';
                          return (
                            <tr key={block.month_key}>
                              <td className="px-3 py-2 text-sm text-gray-900">{getPeriodLabel(block)}</td>
                              <td className="px-3 py-2 text-sm text-right text-gray-700">{days}</td>
                              <td className="px-3 py-2 text-sm text-gray-700 font-mono">{formula}</td>
                              <td className="px-3 py-2 text-sm text-right font-medium text-gray-900">
                                S/ {formatoMonto.format(amt)}
                              </td>
                            </tr>
                          );
                        });
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />

      <ExportProgressModal
        isOpen={showExportProgress}
        title="Generando Excel de resultados"
        progress={exportProgress}
      />
    </div>
  );
};

export default Results;
